import type { Job as TJob } from "@eai-toolkit/client";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Button,
  Grid,
  Typography,
} from "@mui/material";
import { tracker } from "@openreplay/tracker";
import { useCallback, useEffect, useState } from "react";

import { ViewWrapper } from "@components";

import {
  DATA_EXPLORER_ROUTE,
  JOBS_ROUTE,
  LOCAL_STORAGE_KEY_DATA_DATE_RANGE,
  LOCAL_STORAGE_KEY_DATA_FILTER,
  LOCAL_STORAGE_KEY_GET_STARTED_PANEL_CLOSED,
  LOCAL_STORAGE_KEY_GET_STARTED_PANEL_HIDDEN,
  LOCAL_STORAGE_KEY_JOBS_DATE_RANGE,
  LOCAL_STORAGE_KEY_JOBS_FILTER,
} from "@helpers/constants";
import { DASHBOARD_EVENTS } from "@helpers/events";

import { DataResourcesTable } from "../components/DataResourcesTable";
import type { RequestState } from "../components/DataTable";
import { FilterChips } from "../components/FilterChips";
import { JobsTable } from "../components/JobsTable";
import Welcome from "../components/Welcome";
import { getData, getJobs } from "../utils/api";

const Dashboard = () => {
  const [jobs, setJobs] = useState<TJob[]>([]);
  const [dataResources, setDataResources] = useState<any[]>([]);
  const [jobsRequestState, setJobsRequestState] =
    useState<RequestState>("IDLE");
  const [dataRequestState, setDataRequestState] =
    useState<RequestState>("IDLE");
  const [jobsError, setJobsError] = useState<string | null>(null);
  const [dataError, setDataError] = useState<string | null>(null);

  const [jobsFilter, setJobsFilter] = useState<"me" | "cluster">(() => {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY_JOBS_FILTER);
    return stored === "me" || stored === "cluster" ? stored : "me";
  });

  const [jobsDateRange, setJobsDateRange] = useState<
    "today" | "thisWeek" | null
  >(() => {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY_JOBS_DATE_RANGE);
    return stored === "today" || stored === "thisWeek" ? stored : null;
  });

  const [dataFilter, setDataFilter] = useState<"me" | "cluster">(() => {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY_DATA_FILTER);
    return stored === "me" || stored === "cluster" ? stored : "me";
  });

  const [dataDateRange, setDataDateRange] = useState<
    "today" | "thisWeek" | null
  >(() => {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY_DATA_DATE_RANGE);
    return stored === "today" || stored === "thisWeek" ? stored : null;
  });

  const handleJobsFilterChange = (newFilter: "me" | "cluster") => {
    tracker.event(DASHBOARD_EVENTS.FILTER_CHIP_TOGGLED, `jobs:${newFilter}`);
    setJobsFilter(newFilter);
    localStorage.setItem(LOCAL_STORAGE_KEY_JOBS_FILTER, newFilter);
    fetchJobs({ by: newFilter });
  };

  const handleJobsDateRangeChange = (
    newDateRange: "today" | "thisWeek" | null,
  ) => {
    tracker.event(
      DASHBOARD_EVENTS.FILTER_CHIP_TOGGLED,
      `jobs:${newDateRange ?? "none"}`,
    );
    setJobsDateRange(newDateRange);
    if (newDateRange) {
      localStorage.setItem(LOCAL_STORAGE_KEY_JOBS_DATE_RANGE, newDateRange);
    } else {
      localStorage.removeItem(LOCAL_STORAGE_KEY_JOBS_DATE_RANGE);
    }
  };

  const handleDataFilterChange = (newFilter: "me" | "cluster") => {
    tracker.event(DASHBOARD_EVENTS.FILTER_CHIP_TOGGLED, `data:${newFilter}`);
    setDataFilter(newFilter);
    localStorage.setItem(LOCAL_STORAGE_KEY_DATA_FILTER, newFilter);
    fetchData({ by: newFilter });
  };

  const handleDataDateRangeChange = (
    newDateRange: "today" | "thisWeek" | null,
  ) => {
    tracker.event(
      DASHBOARD_EVENTS.FILTER_CHIP_TOGGLED,
      `data:${newDateRange ?? "none"}`,
    );
    setDataDateRange(newDateRange);
    if (newDateRange) {
      localStorage.setItem(LOCAL_STORAGE_KEY_DATA_DATE_RANGE, newDateRange);
    } else {
      localStorage.removeItem(LOCAL_STORAGE_KEY_DATA_DATE_RANGE);
    }
  };

  const fetchJobs = useCallback(
    async (filter: { by: "me" | "cluster" }) => {
      setJobsRequestState("LOADING");
      setJobsError(null);
      try {
        const jobs = await getJobs({
          by: filter.by,
          dateRange: jobsDateRange || undefined,
        });
        if (jobs) {
          setJobs(jobs);
          setJobsRequestState("SUCCESS");
        }
      } catch (error) {
        console.error("Error fetching jobs:", error);
        setJobsError(
          error instanceof Error ? error.message : "Failed to fetch jobs",
        );
        setJobsRequestState("ERROR");
      }
    },
    [jobsDateRange],
  );

  const fetchData = useCallback(
    async (filter: { by: "me" | "cluster" }) => {
      setDataRequestState("LOADING");
      setDataError(null);
      try {
        const data = await getData({
          by: filter.by,
          dateRange: dataDateRange || undefined,
        });
        if (data) {
          setDataResources(data);
          setDataRequestState("SUCCESS");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setDataError(
          error instanceof Error ? error.message : "Failed to fetch data",
        );
        setDataRequestState("ERROR");
      }
    },
    [dataDateRange],
  );

  const handleHideGetStarted = () => {
    tracker.event(DASHBOARD_EVENTS.GET_STARTED_PANEL_HIDDEN);
    setGetStartedPanelToggledClose(!isGetStartedPanelToggledClose);
    setGetStartedPanelHidden(true);
    localStorage.setItem(LOCAL_STORAGE_KEY_GET_STARTED_PANEL_HIDDEN, "true");
  };

  const handleCloseGetStarted = () => {
    const newState = !isGetStartedPanelToggledClose;
    setGetStartedPanelToggledClose(newState);
    localStorage.setItem(
      LOCAL_STORAGE_KEY_GET_STARTED_PANEL_CLOSED,
      String(newState),
    );
  };

  const [isGetStartedPanelHidden, setGetStartedPanelHidden] = useState(
    localStorage.getItem(LOCAL_STORAGE_KEY_GET_STARTED_PANEL_HIDDEN) === "true",
  );

  const [isGetStartedPanelToggledClose, setGetStartedPanelToggledClose] =
    useState(
      localStorage.getItem(LOCAL_STORAGE_KEY_GET_STARTED_PANEL_CLOSED) ===
        "true",
    );

  useEffect(() => {
    fetchJobs({ by: jobsFilter });
  }, [fetchJobs, jobsFilter]);

  useEffect(() => {
    fetchData({ by: dataFilter });
  }, [fetchData, dataFilter]);

  return (
    <ViewWrapper title="Welcome to AI Toolkit">
      {!isGetStartedPanelHidden && (
        <Accordion
          expanded={!isGetStartedPanelToggledClose}
          sx={{
            marginBottom: 4,
            borderBottomLeftRadius: "4px",
            borderBottomRightRadius: "4px",
          }}
          onChange={handleCloseGetStarted}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
          >
            <Typography variant="h4">
              Get started with the AI Toolkit Console
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: `0 16px 16px` }}>
            <Welcome layout="row" />
          </AccordionDetails>
          <AccordionActions sx={{ padding: `0 16px 16px` }}>
            <Button
              variant="text"
              color="primary"
              onClick={handleCloseGetStarted}
            >
              Close
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleHideGetStarted}
            >
              Don&apos;t show Get Started panel again
            </Button>
          </AccordionActions>
        </Accordion>
      )}
      <Grid container spacing={2}>
        <Grid item md={12} lg={6}>
          <FilterChips
            title="Recent Jobs"
            filter={jobsFilter}
            dateRange={jobsDateRange}
            onFilterChange={handleJobsFilterChange}
            onDateRangeChange={handleJobsDateRangeChange}
            seeAllLink={JOBS_ROUTE}
          />
          <JobsTable
            jobs={jobs}
            requestState={jobsRequestState}
            error={jobsError}
          />
        </Grid>
        <Grid item md={12} lg={6}>
          <FilterChips
            title="Recent Data Objects"
            filter={dataFilter}
            dateRange={dataDateRange}
            onFilterChange={handleDataFilterChange}
            onDateRangeChange={handleDataDateRangeChange}
            seeAllLink={DATA_EXPLORER_ROUTE}
            filterTextOverrides={{
              me: "Created by me",
            }}
          />
          <DataResourcesTable
            dataResources={dataResources}
            requestState={dataRequestState}
            error={dataError}
          />
        </Grid>
      </Grid>
      {/* TODO:Fill out monitoring section */}
      {/* <Section
        title={
          <MonitoringTitle>
            <div>
              <Typography variant="h4">Resources</Typography>
            </div>
          </MonitoringTitle>
        }
        sx={(theme) => ({
          mt: 2,
          gridColumn: "span 3",
          [theme.breakpoints.up("md")]: { gridColumn: "span 1" },
        })}
        sxChildren={{
          gap: "1rem",
          display: "grid",
          justifyContent: "start",
          gridTemplateColumns: "repeat(auto-fill, 170px)",
        }}
      >
        resources
      </Section> */}
    </ViewWrapper>
  );
};

export { Dashboard };
